import { useState, useRef, useMemo, useEffect } from 'react'

import scrollToElement from 'scroll-to-element'
import useNonInitialEffect from '@aytm/app/stats/page/components/hooks/useNonInitialEffect'

import { AbortController as _abortControllerPolyfill } from 'abortcontroller-polyfill/dist/cjs-ponyfill'

import { convertTraits } from '@aytm/app/stats/lib/query/traits'
import { getRejectsAndKudos } from '@aytm/app/stats/page/comments/lib/utils'
import {
  DEFAULT_FETCH_TIMEOUT,
  FETCH_TIMEOUT_STEP,
  MAX_FETCH_TIMEOUT,
  FETCH_POLL_BUDGET_MS,
  FETCH_REQUEST_TIMEOUT_MS,
  FETCH_TOO_LONG_MESSAGE
} from '@aytm/app/stats/page/comments/lib/constants'
import { setPageData, setQAMode, updateQACounter } from '@aytm/app/stats/page/comments/actions'
import { fetchSearchResults } from '@aytm/app/stats/page/comments/apiCalls'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { getModelValue, loadQuestionsError } from '@aytm/app/stats/lib/stat-page'

/* compat/compat disabled because polyfill is used */
const AbortController = window.AbortController || _abortControllerPolyfill

let fetchTimeout = DEFAULT_FETCH_TIMEOUT
let fetchDebounceTimeout = 0
let fetchTimer = null
let nocache = false
let unknownStatusRetried = false

const useFetchDataComments = ({
  apiKey,
  dispatch,
  dispatchCommentStatuses,
  searchStateRef,
  propsRef,
  responsesOptionsRef,
  surveyData,
  survey,
  translation,
  specialMode,
  isVirtualOrXpertInScope,
  isOnlyVirtualOrXpert,
  setSurvey,
  onFetchSchema,
  onFetch,
  onFetchApiKey,
  onQAModeChange
}) => {
  const { request, qaCounter, qaTotal, totalRespondents, isQAMode, isQAModeDelayed, isPreserveState } =
    searchStateRef.current
  const { query, selectedQuestion, pageSize, orderBy, orderDirection, currentPage, uidsToApprove } = request

  const [fetchNewApiKey, setFetchNewApiKey] = useState(false)

  const abortController = useRef(null)

  const skipFetchData = useRef(false)

  const isOnePerPage = useMemo(() => isQAMode && 1 == pageSize, [isQAMode, pageSize])

  // The last schema a reply carried, as served. The surface's own survey holds it processed, and
  // processing it again drops every trait, so a reply that carries none falls back to this copy.
  const servedSurveyRef = useRef(null)

  const fetchDataCallback = async data => {
    const { cs, totals } = data

    // Only the first load and an explicit refresh ask for a schema, so most replies say nothing
    // about the survey. Silence is not a statement that it reverted -- but the prop is a snapshot of
    // the page's survey taken when this surface mounted, and this mode never refreshes it, so
    // reading it here undoes the last schema the surface was served. What it already holds is newer.
    if (data.survey) servedSurveyRef.current = data.survey
    const _survey = data.survey || servedSurveyRef.current || surveyData.survey || {}

    if (isQAMode && null === qaCounter) {
      const qaCounterLocalStorage = parseInt(window.localStorage.getItem(`qaCounter${(survey || _survey).id}`), 10)
      dispatch({
        type: 'SET_PARAMS',
        request: {
          value: {
            qaCounter: null !== totals.qaModeCounter ? totals.qaModeCounter : qaCounterLocalStorage || 0
          }
        }
      })
    }
    dispatch({ type: 'SET_PARAM', request: { name: 'isProcessing', value: true } })

    const assets = data.assets || _survey.assets || {}
    const traits = convertTraits(_survey)

    let codeBooks = _survey.codeBooks
    if (!Array.isArray(codeBooks)) {
      codeBooks = Object.keys(codeBooks || {}).reduce(
        (acc, cur) => acc.concat(codeBooks[cur].map(codebook => Object.assign({ type: cur }, codebook))),
        []
      )
    }

    await setSurvey(
      Object.assign({}, _survey, {
        assets,
        traits,
        codeBooks
      })
    )

    dispatch({ type: 'SEARCH_RESULTS', data: { data, specialMode, pageSize } })
    dispatchCommentStatuses({
      type: 'BATCH_DATA',
      data: { batchData: getRejectsAndKudos(cs), isPreserveState }
    })
    dispatch({ type: 'SET_PARAM', request: { name: 'isPreserveState', value: false } })

    fetchTimeout = DEFAULT_FETCH_TIMEOUT
  }

  const tooLong = () => {
    fetchTimeout = DEFAULT_FETCH_TIMEOUT
    dispatch({ type: 'FAILURE', error: { name: 'CustomError', message: FETCH_TOO_LONG_MESSAGE } })
    // nothing else would take the page off its loading screen
    if (getModelValue('loading')) loadQuestionsError()
  }

  const fetchData = async (
    {
      includeSchema = false,
      customParams = {},
      customFetchDataCallback = null,
      pollStartedAt = null,
      requestRetried = false
    } = {},
    fetchDataComplete = null
  ) => {
    let requestTimedOut = false
    let requestTimer = null

    try {
      if (null !== abortController.current) {
        abortController.current.abort()
        abortController.current = null
      }

      abortController.current = new AbortController()

      const { signal } = abortController.current
      const controller = abortController.current
      // a request that never answers is aborted here rather than left to hold the loader forever
      requestTimer = setTimeout(() => {
        requestTimedOut = true
        controller.abort()
      }, FETCH_REQUEST_TIMEOUT_MS)

      const data = await fetchSearchResults({
        query,
        selectedQuestion,
        pageSize,
        orderBy,
        orderDirection,
        translation,
        currentPage,
        isQAMode,
        nocache,
        // eslint-disable-next-line no-param-reassign
        includeSchema: (includeSchema ||= surveyData.survey == undefined && !customFetchDataCallback),
        uidsToApprove,
        signal,
        ...customParams
      })
      clearTimeout(requestTimer)

      if (isQAMode && (nocache || !isOnePerPage)) {
        dispatch({ type: 'SET_PARAM', request: { name: 'isUpdateQATotal', value: true } })
        if (null !== uidsToApprove) {
          updateQACounter({ dispatch, qaCounter, qaTotal, uidsToApprove })
          dispatch({ type: 'SET_REQUEST_PARAM', request: { name: 'uidsToApprove', value: null } })
        }
      }

      nocache = false

      const callback = _data => {
        dispatch({ type: 'SET_PARAM', request: { name: 'isLoading', value: false } })

        fetchDataCallback(_data)
        if (fetchDataComplete && 'function' == typeof fetchDataComplete) {
          fetchDataComplete()
        }
      }

      switch (data.status) {
        case 'OK': {
          unknownStatusRetried = false

          if (null !== customFetchDataCallback) {
            dispatch({ type: 'SET_PARAM', request: { name: 'isLoading', value: false } })

            customFetchDataCallback(data)
          } else {
            if (
              PageGlobals.SHARE_KEY == undefined &&
              data.survey != undefined &&
              data.survey.target_market?.quotas == undefined
            ) {
              if (onFetchSchema && 'function' == typeof onFetchSchema) {
                onFetchSchema(callback, data)
              }
            } else {
              callback(data)

              if (onFetch && 'function' == typeof onFetch) {
                onFetch(data)
              }
            }
          }
          break
        }
        case 'RUNNING': {
          const startedAt = pollStartedAt ?? Date.now()
          if (FETCH_POLL_BUDGET_MS <= Date.now() - startedAt) {
            tooLong()
            break
          }

          fetchTimeout = Math.min(MAX_FETCH_TIMEOUT, fetchTimeout + FETCH_TIMEOUT_STEP)
          fetchTimer = setTimeout(() => {
            fetchTimer = null
            fetchData(
              { includeSchema, customParams, customFetchDataCallback, pollStartedAt: startedAt },
              fetchDataComplete
            )
          }, fetchTimeout)
          break
        }
        case 'NO SCHEMA': {
          if (onFetchSchema && 'function' == typeof onFetchSchema) {
            onFetchSchema(callback, data)
          }
          break
        }
        case 'INVALID KEY': {
          if (onFetchApiKey && 'function' == typeof onFetchApiKey) {
            onFetchApiKey(fetchDataComplete)
          }
          break
        }
        default: {
          // the data service can drop a job it reported as running; a fresh key usually recovers it
          if (!unknownStatusRetried && onFetchApiKey && 'function' == typeof onFetchApiKey) {
            unknownStatusRetried = true
            onFetchApiKey(fetchDataComplete)
            break
          }

          unknownStatusRetried = false
          dispatch({ type: 'FAILURE', error: { name: 'CustomError', message: 'Something happen' } })
          // nothing else would take the page off its loading screen
          if (getModelValue('loading')) loadQuestionsError()
        }
      }
      fetchDebounceTimeout = 0

      abortController.current = null
    } catch (error) {
      clearTimeout(requestTimer)

      if (requestTimedOut) {
        abortController.current = null
        fetchDebounceTimeout = 0
        // one more ask before the page gives up: a single hung request is not a dead service
        if (!requestRetried) {
          fetchData(
            { includeSchema, customParams, customFetchDataCallback, pollStartedAt, requestRetried: true },
            fetchDataComplete
          )
        } else tooLong()
        return
      }

      if (!error.name || (error.name && 'AbortError' !== error.name)) {
        dispatch({ type: 'FAILURE', error })
      }

      fetchTimeout = DEFAULT_FETCH_TIMEOUT
      fetchDebounceTimeout = 0
    }
  }

  const fetchDataDelay = (force = false, fetchDataComplete) => {
    fetchTimer && clearTimeout(fetchTimer)

    if (null !== abortController.current) {
      abortController.current.abort()
      abortController.current = null
    }

    // without a key nothing is asked, so nothing would ever clear a loading mark set here
    if (apiKey == undefined || null === apiKey) return

    dispatch({ type: 'SEARCH_INIT' })

    const callback = () => {
      fetchTimer = null

      if (force || isQAMode) {
        nocache = true
      }

      fetchData({ includeSchema: force }, fetchDataComplete)
    }

    if (0 < fetchDebounceTimeout) {
      fetchTimer = setTimeout(callback, fetchDebounceTimeout)
    } else callback()
  }

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const fetchDataDestroy = () => {
    fetchTimeout = DEFAULT_FETCH_TIMEOUT
    fetchDebounceTimeout = 1000

    fetchTimer && clearTimeout(fetchTimer)

    if (null !== abortController.current) {
      abortController.current.abort()
      abortController.current = null
    }
  }

  // normal mode fetch data effect
  useEffect(() => {
    if (isQAMode) {
      if (isQAModeDelayed) {
        // setQAMode(searchStateRef.current.isQAMode)
        setQAMode({
          dispatch,
          pageSize,
          currentPage,
          totalRespondents,
          isQAMode,
          onQAModeChange,
          fetchDataDelay
        })
        dispatch({ type: 'SET_PARAM', request: { name: 'isQAModeDelayed', value: false } })
      } else {
        fetchDataDelay()
      }
    } else fetchDataDelay(true)

    return () => (isQAMode && isQAModeDelayed ? null : fetchDataDestroy())
    // disabled: fetchDataDelay is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey])

  // normal mode fetch data effect
  useNonInitialEffect(() => {
    if (skipFetchData.current) {
      skipFetchData.current = false
      return
    }

    if (!isQAMode) {
      if (fetchNewApiKey) {
        setFetchNewApiKey(false)

        dispatch({ type: 'SEARCH_INIT' })
        onFetchApiKey()
      } else fetchDataDelay()
    }

    return () => (isQAMode ? null : fetchDataDestroy())
    // disabled: fetchDataDelay is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedQuestion, pageSize, orderBy, orderDirection, currentPage])

  // normal mode fetch data effect
  useNonInitialEffect(() => {
    if (query.some(item => 'plain' == item.type) && !isQAMode) {
      if (1 != currentPage) {
        skipFetchData.current = true
        dispatch({
          type: 'SET_REQUEST_PARAM_SET',
          request: { currentPage: 1 }
        })
      }
      dispatch({ type: 'SEARCH_INIT' })
      onFetchApiKey()
    } else if (isVirtualOrXpertInScope || isOnlyVirtualOrXpert) {
      dispatch({ type: 'SEARCH_INIT' })
      onFetchApiKey()
    } else setFetchNewApiKey(true)

    return () => (isQAMode ? null : fetchDataDestroy())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translation])

  // QA mode fetch data effect for both pageSize modes in QA
  useNonInitialEffect(() => {
    if (isQAMode) {
      setPageData({ dispatch, pageData: [] })
      fetchDataDelay()
    }

    return () => (isQAMode ? fetchDataDestroy() : null)
    // disabled: fetchDataDelay is const, fetchDataDestroy is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize])

  // QA mode fetch data effect for pageSize > 1
  useNonInitialEffect(() => {
    if (isQAMode && !isOnePerPage) {
      fetchDataDelay()
      scrollToElement('.-header', {
        offset: 0,
        align: 'top',
        duration: 250
      })
    }

    return () => (isQAMode && !isOnePerPage ? fetchDataDestroy() : null)
    // disabled: isOnePerPage is const, fetchDataDestroy is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, orderDirection, currentPage])

  // QA mode fetch data effect for pageSize = 1 and changed qaCounter
  useNonInitialEffect(() => {
    if (isOnePerPage && null !== qaCounter && responsesOptionsRef.current.pageData) {
      const newPageData = [...responsesOptionsRef.current.pageData.slice(1)]
      dispatch({ type: 'SET_PARAM', request: { name: 'pageData', value: newPageData } })
      if (3 >= newPageData.length) fetchDataDelay()
    }
    // disabled: fetchDataDelay is const, isOnePerPage is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qaCounter])

  useNonInitialEffect(() => {
    if ('completes' !== propsRef.current.loadRejectedCS && isQAMode) {
      propsRef.current.onUpdateParams(null, 'completes', specialMode)
      dispatch({ type: 'SET_PARAM', request: { name: 'isQAModeDelayed', value: true } })
    } else {
      setQAMode({
        dispatch,
        pageSize,
        currentPage,
        totalRespondents,
        isQAMode,
        onQAModeChange,
        fetchDataDelay
      })
    }
    // disabled: isQAMode is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQAMode])

  return { fetchData, fetchDataDelay }
}

export default useFetchDataComments
